import React, { useEffect, useMemo, useRef, useState } from 'react';
import './NotionTable.css';

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

export type NotionColumnType =
  | 'title'
  | 'text'
  | 'select'
  | 'multi'
  | 'number'
  | 'date'
  | 'person'
  | 'url'
  | 'checkbox';

export type NotionPillColor =
  | 'default'
  | 'gray'
  | 'brown'
  | 'orange'
  | 'yellow'
  | 'green'
  | 'blue'
  | 'purple'
  | 'pink'
  | 'red';

export type NotionColumn<T> = {
  key: string;
  label: string;
  type: NotionColumnType;
  /** Largeur CSS (ex. 220 ou '1fr'). */
  width?: number | string;
  /** Valeur brute utilisée pour le tri et la recherche. */
  accessor: (row: T) => string | number | Date | null | undefined;
  /** Rendu personnalisé de la cellule (par défaut : valeur brute formatée). */
  render?: (row: T) => React.ReactNode;
  /** Désactive le tri sur cette colonne. */
  sortable?: boolean;
  /** Alignement du contenu. */
  align?: 'left' | 'right';
};

export type NotionView = {
  key: string;
  label: string;
  icon?: React.ReactNode;
  count?: number;
};

export type NotionTableLabels = {
  search: string;
  sort: string;
  filter: string;
  open: string;
  count: string;
  empty: string;
  clearSort: string;
  ascending: string;
  descending: string;
};

type SortState = { key: string; dir: 'asc' | 'desc' } | null;

type Props<T> = {
  columns: NotionColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  views?: NotionView[];
  activeView?: string;
  onViewChange?: (key: string) => void;
  /** Bouton bleu "Nouveau" à droite de la barre d'outils. */
  newLabel?: string;
  onNew?: () => void;
  /** Déclenché par le bouton "OUVRIR" au survol du titre ou par double‑clic. */
  onOpen?: (row: T) => void;
  /** Tri initial. */
  defaultSort?: { key: string; dir: 'asc' | 'desc' };
  labels: NotionTableLabels;
  /** Contenu libre sous la barre de vues (ex. : filtres actifs). */
  toolbarExtra?: React.ReactNode;
  loading?: boolean;
  className?: string;
  /** Formatage des dates (sinon toLocaleDateString). */
  formatDate?: (d: Date) => string;
  /** Cases à cocher sur les lignes visibles. */
  selectable?: boolean;
  selectedKeys?: string[];
  onSelectedKeysChange?: (keys: string[]) => void;
};

/* ------------------------------------------------------------------ */
/* Icônes (style Notion, monochromes)                                   */
/* ------------------------------------------------------------------ */

const I = {
  table: (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
      <path d="M2 3.5A1.5 1.5 0 0 1 3.5 2h9A1.5 1.5 0 0 1 14 3.5v9a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 2 12.5v-9Zm1.2 1.7v2.1h3.7V5.2H3.2Zm4.9 0v2.1h4.7V5.2H8.1Zm4.7 3.3H8.1v2.1h4.7V8.5Zm-5.9 2.1V8.5H3.2v2.1h3.7Z" fill="currentColor" />
    </svg>
  ),
  title: (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
      <path d="M1.8 12.5 5.1 3.3h1.6l3.3 9.2H8.4l-.8-2.4H4.2l-.8 2.4H1.8Zm2.8-3.6h2.6L5.9 5.1 4.6 8.9Zm7 3.7c-1.3 0-2.1-.7-2.1-1.8 0-1.2.9-1.8 2.6-1.9l1.3-.1v-.4c0-.6-.4-.9-1.1-.9-.6 0-1 .2-1.2.7l-1.3-.2c.2-1 1.2-1.6 2.6-1.6 1.6 0 2.4.7 2.4 2v4.1h-1.3v-.8c-.4.6-1 .9-1.9.9Zm.4-1c.8 0 1.4-.5 1.4-1.2v-.5l-1.1.1c-.9.1-1.3.4-1.3.9s.4.7 1 .7Z" fill="currentColor" />
    </svg>
  ),
  text: (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
      <path d="M2.5 4.2h11v1.3h-11V4.2Zm0 3.1h11v1.3h-11V7.3Zm0 3.1h7v1.3h-7v-1.3Z" fill="currentColor" />
    </svg>
  ),
  select: (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
      <circle cx="8" cy="8" r="5.6" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <path d="M5.6 7.2 8 9.6l2.4-2.4" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  multi: (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
      <path d="M2.5 4h11v1.4h-11V4Zm0 3.3h11v1.4h-11V7.3Zm0 3.3h11V12h-11v-1.4Z" fill="currentColor" />
      <circle cx="3.2" cy="4.7" r="1.1" fill="currentColor" />
    </svg>
  ),
  number: (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
      <path d="M6.1 2.5 5.4 6h2.4l.7-3.5h1.4L9.2 6h2.1v1.3H8.9l-.4 2.1h2.1v1.3H8.3l-.6 2.8H6.3l.6-2.8H4.5l-.6 2.8H2.5l.6-2.8H1.2V9.4h2.2l.4-2.1H1.9V6h2.2l.7-3.5h1.3Zm-1 4.8-.4 2.1h2.4l.4-2.1H5.1Z" fill="currentColor" />
    </svg>
  ),
  date: (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
      <path d="M4.5 1.8v1.4h7V1.8h1.4v1.4h.8c.7 0 1.3.6 1.3 1.3v8.2c0 .7-.6 1.3-1.3 1.3H2.3c-.7 0-1.3-.6-1.3-1.3V4.5c0-.7.6-1.3 1.3-1.3h.8V1.8h1.4Zm-2.1 5v5.8h11.2V6.8H2.4Z" fill="currentColor" />
    </svg>
  ),
  person: (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
      <circle cx="8" cy="5.2" r="2.8" fill="currentColor" />
      <path d="M2.4 13.4c.4-2.7 2.7-4.3 5.6-4.3s5.2 1.6 5.6 4.3H2.4Z" fill="currentColor" />
    </svg>
  ),
  url: (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
      <path d="M6.6 9.4a2.6 2.6 0 0 0 3.7 0l2.2-2.2a2.6 2.6 0 0 0-3.7-3.7l-.9.9m1.5 1.4a2.6 2.6 0 0 0-3.7 0L3.5 8a2.6 2.6 0 0 0 3.7 3.7l.9-.9" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  ),
  checkbox: (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
      <rect x="2.5" y="2.5" width="11" height="11" rx="2" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <path d="m5.2 8.2 1.9 1.9 3.7-4" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  page: (
    <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
      <path d="M4 1.5h5.2L13 5.3v8.2c0 .6-.4 1-1 1H4c-.6 0-1-.4-1-1v-11c0-.6.4-1 1-1Zm4.8 1.2v3h3l-3-3Z" fill="currentColor" opacity="0.75" />
      <path d="M5.2 8h5.6v1H5.2V8Zm0 2.2h5.6v1H5.2v-1Z" fill="#fff" />
    </svg>
  ),
  open: (
    <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
      <path d="M9.5 2h4.5v4.5M14 2 8.5 7.5M12 9.5v3.3c0 .7-.5 1.2-1.2 1.2H3.2C2.5 14 2 13.5 2 12.8V5.2C2 4.5 2.5 4 3.2 4h3.3" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  filter: (
    <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true">
      <path d="M2.5 4h11M4.5 8h7M6.5 12h3" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  sort: (
    <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true">
      <path d="M5 2.5v11m0 0L2.5 11M5 13.5 7.5 11M11 13.5v-11m0 0L8.5 5M11 2.5 13.5 5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  bolt: (
    <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true">
      <path d="M9 1.5 3.5 9H8l-1 5.5L12.5 7H8l1-5.5Z" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  ),
  search: (
    <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true">
      <circle cx="7" cy="7" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="m10.5 10.5 3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  expand: (
    <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true">
      <path d="M9.5 2.5h4v4m-11 3v4h4m7-11-4.5 4.5M2.5 13.5 7 9" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  caret: (
    <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
      <path d="m4 6 4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  arrowUp: (
    <svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true">
      <path d="M8 13V3m0 0L4 7m4-4 4 4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  arrowDown: (
    <svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true">
      <path d="M8 3v10m0 0 4-4m-4 4-4-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  close: (
    <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
      <path d="m4 4 8 8m0-8-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
};

export const NotionIcons = I;

const columnIcon = (type: NotionColumnType) => {
  switch (type) {
    case 'title': return I.title;
    case 'text': return I.text;
    case 'select': return I.select;
    case 'multi': return I.multi;
    case 'number': return I.number;
    case 'date': return I.date;
    case 'person': return I.person;
    case 'url': return I.url;
    case 'checkbox': return I.checkbox;
    default: return I.text;
  }
};

/* ------------------------------------------------------------------ */
/* Pill (tag coloré façon Notion)                                       */
/* ------------------------------------------------------------------ */

export const NotionPill: React.FC<{ color?: NotionPillColor; children: React.ReactNode; className?: string }> = ({
  color = 'default',
  children,
  className,
}) => <span className={`nt-pill nt-pill--${color} ${className || ''}`}>{children}</span>;

/* ------------------------------------------------------------------ */
/* Avatar "personne"                                                    */
/* ------------------------------------------------------------------ */

export const NotionPerson: React.FC<{ name: string; sub?: string }> = ({ name, sub }) => {
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  let hue = 0;
  for (let i = 0; i < name.length; i += 1) hue = (hue * 31 + name.charCodeAt(i)) % 360;
  return (
    <span className="nt-person">
      <span className="nt-person-avatar" style={{ background: `hsl(${hue} 45% 82%)`, color: `hsl(${hue} 45% 28%)` }}>
        {initial}
      </span>
      <span className="nt-person-text">
        <span className="nt-person-name">{name}</span>
        {sub && <span className="nt-person-sub">{sub}</span>}
      </span>
    </span>
  );
};

/* ------------------------------------------------------------------ */
/* Table                                                                */
/* ------------------------------------------------------------------ */

function compare(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

function toSearchable(v: unknown): string {
  if (v == null) return '';
  if (v instanceof Date) return v.toLocaleDateString();
  return String(v);
}

export function NotionTable<T>({
  columns,
  rows,
  rowKey,
  views,
  activeView,
  onViewChange,
  newLabel,
  onNew,
  onOpen,
  defaultSort,
  labels,
  toolbarExtra,
  loading,
  className,
  formatDate,
  selectable,
  selectedKeys,
  onSelectedKeysChange,
}: Props<T>) {
  const [sort, setSort] = useState<SortState>(defaultSort || null);
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const sortMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (searchOpen) searchRef.current?.focus();
  }, [searchOpen]);

  // Ferme le menu de tri au clic extérieur
  useEffect(() => {
    if (!sortMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!sortMenuRef.current?.contains(e.target as Node)) setSortMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [sortMenuOpen]);

  const fmtDate = formatDate || ((d: Date) => d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }));

  const visibleRows = useMemo(() => {
    let out = rows;
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter((r) => columns.some((c) => toSearchable(c.accessor(r)).toLowerCase().includes(q)));
    }
    if (sort) {
      const col = columns.find((c) => c.key === sort.key);
      if (col) {
        out = [...out].sort((a, b) => {
          const res = compare(col.accessor(a), col.accessor(b));
          return sort.dir === 'asc' ? res : -res;
        });
      }
    }
    return out;
  }, [rows, columns, search, sort]);

  const toggleSort = (key: string) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
  };

  const gridTemplate = [
    selectable ? '36px' : '',
    ...columns.map((c) => (typeof c.width === 'number' ? `${c.width}px` : c.width || 'minmax(140px, 1fr)')),
  ]
    .filter(Boolean)
    .join(' ');

  const colSpan = columns.length + (selectable ? 1 : 0);
  const selected = selectedKeys || [];
  const visibleKeys = visibleRows.map((r) => rowKey(r));
  const allVisibleSelected = visibleKeys.length > 0 && visibleKeys.every((k) => selected.includes(k));
  const someVisibleSelected = visibleKeys.some((k) => selected.includes(k));

  const toggleKey = (key: string) => {
    if (!onSelectedKeysChange) return;
    onSelectedKeysChange(selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key]);
  };

  const toggleVisible = () => {
    if (!onSelectedKeysChange) return;
    if (allVisibleSelected) {
      onSelectedKeysChange(selected.filter((k) => !visibleKeys.includes(k)));
    } else {
      onSelectedKeysChange(Array.from(new Set(selected.concat(visibleKeys))));
    }
  };

  const renderCell = (col: NotionColumn<T>, row: T) => {
    if (col.render) return col.render(row);
    const v = col.accessor(row);
    if (v == null || v === '') return <span className="nt-empty-cell" />;
    if (v instanceof Date) return fmtDate(v);
    if (col.type === 'number') return <span className="nt-num">{typeof v === 'number' ? v.toLocaleString() : v}</span>;
    return String(v);
  };

  const sortedCol = sort ? columns.find((c) => c.key === sort.key) : undefined;

  return (
    <div className={`nt ${className || ''}`}>
      {/* Barre des vues + outils */}
      <div className="nt-topbar">
        <div className="nt-views" role="tablist">
          {(views || []).map((v) => (
            <button
              key={v.key}
              type="button"
              role="tab"
              aria-selected={v.key === activeView}
              className={`nt-view ${v.key === activeView ? 'active' : ''}`}
              onClick={() => onViewChange?.(v.key)}
            >
              <span className="nt-view-icon">{v.icon || I.table}</span>
              {v.label}
              {typeof v.count === 'number' && <span className="nt-view-count">{v.count}</span>}
            </button>
          ))}
        </div>

        <div className="nt-tools">
          <div className="nt-sort-wrap" ref={sortMenuRef}>
            <button
              type="button"
              className={`nt-tool ${sort ? 'active' : ''}`}
              title={labels.sort}
              aria-label={labels.sort}
              onClick={() => setSortMenuOpen((o) => !o)}
            >
              {I.sort}
            </button>
            {sortMenuOpen && (
              <div className="nt-menu">
                {columns
                  .filter((c) => c.sortable !== false)
                  .map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      className={`nt-menu-item ${sort?.key === c.key ? 'active' : ''}`}
                      onClick={() => {
                        toggleSort(c.key);
                      }}
                    >
                      <span className="nt-menu-icon">{columnIcon(c.type)}</span>
                      {c.label}
                      {sort?.key === c.key && (
                        <span className="nt-menu-dir">
                          {sort.dir === 'asc' ? I.arrowUp : I.arrowDown}
                          {sort.dir === 'asc' ? labels.ascending : labels.descending}
                        </span>
                      )}
                    </button>
                  ))}
                {sort && (
                  <>
                    <div className="nt-menu-sep" />
                    <button type="button" className="nt-menu-item nt-menu-item--danger" onClick={() => { setSort(null); setSortMenuOpen(false); }}>
                      <span className="nt-menu-icon">{I.close}</span>
                      {labels.clearSort}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          <div className={`nt-search ${searchOpen || search ? 'open' : ''}`}>
            <button
              type="button"
              className={`nt-tool ${search ? 'active' : ''}`}
              title={labels.search}
              aria-label={labels.search}
              onClick={() => setSearchOpen((o) => !o)}
            >
              {I.search}
            </button>
            <input
              ref={searchRef}
              className="nt-search-input"
              placeholder={labels.search}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onBlur={() => { if (!search) setSearchOpen(false); }}
              onKeyDown={(e) => { if (e.key === 'Escape') { setSearch(''); setSearchOpen(false); } }}
            />
          </div>

          {newLabel && (
            <button type="button" className="nt-new" onClick={onNew}>
              {newLabel}
              <span className="nt-new-caret">{I.caret}</span>
            </button>
          )}
        </div>
      </div>

      {(toolbarExtra || sort || search) && (
        <div className={`nt-subbar ${toolbarExtra ? '' : 'nt-subbar--chips-only'}`}>
          {toolbarExtra}
          {sortedCol && (
            <button type="button" className="nt-chip" onClick={() => setSort(null)}>
              {I.sort}
              <span>{sortedCol.label}</span>
              <span className="nt-chip-dir">{sort?.dir === 'asc' ? labels.ascending : labels.descending}</span>
              {I.close}
            </button>
          )}
          {search && (
            <button type="button" className="nt-chip" onClick={() => setSearch('')}>
              {I.search}
              <span>“{search}”</span>
              {I.close}
            </button>
          )}
        </div>
      )}

      {/* Grille */}
      <div className="nt-scroll">
        <div className="nt-grid" style={{ gridTemplateColumns: gridTemplate }} role="table">
          <div className="nt-row nt-row--head" role="row">
            {selectable && (
              <div className="nt-cell nt-check" role="columnheader">
                <input
                  type="checkbox"
                  className="nt-checkbox"
                  checked={allVisibleSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someVisibleSelected && !allVisibleSelected;
                  }}
                  onChange={toggleVisible}
                  aria-label="select"
                />
              </div>
            )}
            {columns.map((c) => (
              <button
                key={c.key}
                type="button"
                role="columnheader"
                className={`nt-head ${sort?.key === c.key ? 'sorted' : ''} ${c.align === 'right' ? 'right' : ''}`}
                onClick={() => c.sortable !== false && toggleSort(c.key)}
              >
                <span className="nt-head-icon">{columnIcon(c.type)}</span>
                <span className="nt-head-label">{c.label}</span>
                {sort?.key === c.key && <span className="nt-head-dir">{sort.dir === 'asc' ? I.arrowUp : I.arrowDown}</span>}
              </button>
            ))}
          </div>

          {loading && (
            <div className="nt-loading" style={{ gridColumn: `1 / span ${colSpan}` }}>
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="nt-skeleton" style={{ ['--i' as string]: i }} />
              ))}
            </div>
          )}

          {!loading && visibleRows.length === 0 && (
            <div className="nt-empty" style={{ gridColumn: `1 / span ${colSpan}` }}>
              {labels.empty}
            </div>
          )}

          {!loading &&
            visibleRows.map((row) => (
              <div
                key={rowKey(row)}
                className={`nt-row ${selected.includes(rowKey(row)) ? 'nt-row--selected' : ''}`}
                role="row"
                onClick={(e) => {
                  if (!selectable) return;
                  const el = e.target as HTMLElement;
                  if (el.closest('button, a, input, textarea, select, label')) return;
                  toggleKey(rowKey(row));
                }}
                onDoubleClick={() => onOpen?.(row)}
              >
                {selectable && (
                  <div className="nt-cell nt-check" role="cell" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      className="nt-checkbox"
                      checked={selected.includes(rowKey(row))}
                      onChange={() => toggleKey(rowKey(row))}
                      aria-label="select row"
                    />
                  </div>
                )}
                {columns.map((c) => (
                  <div
                    key={c.key}
                    role="cell"
                    className={`nt-cell nt-cell--${c.type} ${c.align === 'right' ? 'right' : ''}`}
                  >
                    {c.type === 'title' ? (
                      <>
                        <span className="nt-page-icon">{I.page}</span>
                        <span className="nt-title-text">{renderCell(c, row)}</span>
                        {onOpen && (
                          <button
                            type="button"
                            className="nt-open"
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpen(row);
                            }}
                          >
                            {I.open}
                            {labels.open}
                          </button>
                        )}
                      </>
                    ) : (
                      renderCell(c, row)
                    )}
                  </div>
                ))}
              </div>
            ))}

          {/* Pied : compteur */}
          <div className="nt-row nt-row--foot" role="row">
            <div className="nt-foot" style={{ gridColumn: `1 / span ${colSpan}` }}>
              <span className="nt-foot-label">{labels.count}</span>
              <span className="nt-foot-value">{visibleRows.length}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NotionTable;
